import { useCallback, useEffect, useRef, useState } from 'react';
import { MAP_MIN_LOT_ZOOM } from '../styles/mapTheme';

const toFinite = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Devuelve los lot_id cuyo pin cae dentro del viewport actual del mapa.
 * Con listas grandes evita pasar cientos de ids al paint de PMTiles.
 */
export const filterLotIdsInMapBounds = (map, lots, { maxCount = Infinity, padding = 0 } = {}) => {
  if (!map || !Array.isArray(lots) || lots.length === 0) return [];

  const bounds = map.getBounds();
  if (!bounds) return [];

  const west = bounds.getWest() - padding;
  const east = bounds.getEast() + padding;
  const south = bounds.getSouth() - padding;
  const north = bounds.getNorth() + padding;

  const ids = [];
  for (const lot of lots) {
    if (ids.length >= maxCount) break;
    const lat = toFinite(lot?.lat ?? lot?.latitude);
    const lng = toFinite(lot?.lng ?? lot?.longitude ?? lot?.lon);
    if (lat == null || lng == null) continue;
    const lotId = lot?.lot_id ?? lot?.id;
    if (!lotId) continue;
    if (lng >= west && lng <= east && lat >= south && lat <= north) {
      ids.push(lotId);
    }
  }
  return ids;
};

/**
 * Mantiene actualizados los ids de lotes visibles en el viewport (debounced en move/zoom).
 */
export function useViewportLotHighlightIds({
  mapInstanceRef,
  lots,
  enabled = false,
  maxCount = 250,
  debounceMs = 120,
}) {
  const [viewportIds, setViewportIds] = useState([]);
  const lotsRef = useRef(lots);
  lotsRef.current = lots;

  const update = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || !enabled) {
      setViewportIds([]);
      return;
    }
    if (map.getZoom() < MAP_MIN_LOT_ZOOM) {
      setViewportIds([]);
      return;
    }
    setViewportIds(filterLotIdsInMapBounds(map, lotsRef.current, { maxCount }));
  }, [mapInstanceRef, enabled, maxCount]);

  useEffect(() => {
    if (!enabled) {
      setViewportIds([]);
      return undefined;
    }

    const map = mapInstanceRef.current;
    if (!map) return undefined;

    let timeoutId = null;
    const scheduleUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(update, debounceMs);
    };

    scheduleUpdate();
    map.on('moveend', scheduleUpdate);
    map.on('zoomend', scheduleUpdate);

    return () => {
      clearTimeout(timeoutId);
      map.off('moveend', scheduleUpdate);
      map.off('zoomend', scheduleUpdate);
    };
  }, [mapInstanceRef, enabled, update, debounceMs, lots]);

  return viewportIds;
}
